import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" }
const _hoisted_2 = { class: "mb-6" }
const _hoisted_3 = { class: "flex items-center gap-3" }
const _hoisted_4 = { class: "p-2.5 bg-orange-50 rounded-xl" }
const _hoisted_5 = { class: "bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 flex items-start gap-3" }
const _hoisted_6 = { class: "grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" }
const _hoisted_7 = { class: "bg-white border border-dashboard-border rounded-xl p-4" }
const _hoisted_8 = { class: "text-2xl font-bold text-dashboard-text" }
const _hoisted_9 = { class: "bg-white border border-dashboard-border rounded-xl p-4" }
const _hoisted_10 = { class: "text-2xl font-bold text-dashboard-text" }
const _hoisted_11 = { class: "bg-white border border-dashboard-border rounded-xl p-4" }
const _hoisted_12 = { class: "text-sm font-medium text-dashboard-text mt-1" }
const _hoisted_13 = { class: "bg-white border border-dashboard-border rounded-xl shadow-sm p-4 mb-5" }
const _hoisted_14 = { class: "text-sm font-semibold text-dashboard-text mb-3 flex items-center gap-2" }
const _hoisted_15 = { class: "flex items-end gap-3 flex-wrap" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = { class: "bg-white border border-dashboard-border rounded-xl shadow-sm overflow-hidden" }
const _hoisted_18 = { class: "px-4 py-3 border-b border-dashboard-border flex items-center justify-between" }
const _hoisted_19 = { class: "text-sm font-semibold text-dashboard-text flex items-center gap-2" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = {
  key: 0,
  class: "p-12 text-center"
}
const _hoisted_22 = {
  key: 1,
  class: "p-6"
}
const _hoisted_23 = { class: "bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" }
const _hoisted_24 = { class: "text-sm text-red-600 mt-1" }
const _hoisted_25 = {
  key: 2,
  class: "p-12 text-center"
}
const _hoisted_26 = {
  key: 3,
  class: "divide-y divide-dashboard-border"
}
const _hoisted_27 = { class: "p-2 bg-orange-50 rounded-lg flex-shrink-0" }
const _hoisted_28 = { class: "flex-1 min-w-0" }
const _hoisted_29 = { class: "font-medium text-dashboard-text" }
const _hoisted_30 = { class: "text-xs text-dashboard-muted truncate font-mono" }
const _hoisted_31 = { class: "text-right text-xs text-dashboard-muted hidden sm:block" }
const _hoisted_32 = { key: 0 }
const _hoisted_33 = ["href"]

import { ref, computed, onMounted } from 'vue'
import {
  ArrowLeft, Archive as ArchiveIcon, Info, Zap, Upload, RefreshCw, AlertCircle,
  Calendar, Download,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch, API_BASE_URL } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import { formatBytes } from '@/utils/auditMeta'

interface ArchiveItem {
  year: number
  month: number
  filename: string
  path: string
  size_bytes: number | null
  modified_at: string | null
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AuditArchivesView',
  setup(__props) {

const toast = useToast()

const archives = ref<ArchiveItem[]>([])
const loading = ref(false)
const error = ref('')
const exporting = ref(false)
const exportMonth = ref(currentMonthMinusOne())

function currentMonthMinusOne(): string {
  const now = new Date()
  const month = now.getMonth() === 0 ? 12 : now.getMonth()
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
  return `${year}-${String(month).padStart(2, '0')}`
}

const totalSize = computed(() =>
  archives.value.reduce((sum, a) => sum + (a.size_bytes || 0), 0),
)

const lastExportDate = computed(() => {
  const mostRecent = archives.value
    .filter(a => a.modified_at)
    .sort((a, b) => (b.modified_at! < a.modified_at! ? -1 : 1))[0]
  return mostRecent?.modified_at ? formatDate(mostRecent.modified_at) : null
})

function monthName(year: number, month: number): string {
  const names = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
  return `${names[month - 1]} ${year}`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function downloadUrl(year: number, month: number): string {
  return `${API_BASE_URL}/api/admin/audit/archives/${year}/${String(month).padStart(2, '0')}/download/`
}

async function fetchArchives() {
  loading.value = true
  error.value = ''
  try {
    const result = await apiFetch('/api/admin/audit/archives/')
    archives.value = result.archives || []
  } catch (e: any) {
    error.value = e?.message || 'Erreur de chargement'
    toast.error('Erreur', error.value)
  } finally {
    loading.value = false
  }
}

async function triggerExport() {
  if (!exportMonth.value) return
  if (!confirm(`Lancer l'export du mois ${exportMonth.value} ?\nL'opération peut prendre quelques secondes.`)) return

  exporting.value = true
  try {
    const result = await apiFetch('/api/admin/audit/archives/export/', {
      method: 'POST',
      body: JSON.stringify({ month: exportMonth.value }),
    })
    if (result.count === 0) {
      toast.show({ type: 'info', title: 'Aucune donnée', message: result.message || `Pas d'entrées pour ${exportMonth.value}` })
    } else {
      toast.success('Export réussi', `${result.count} entrée(s) exportée(s) (${formatBytes(result.size_bytes)})`)
    }
    await fetchArchives()
  } catch (e: any) {
    toast.error('Erreur export', e?.message || 'Échec de l\'export')
  } finally {
    exporting.value = false
  }
}

onMounted(fetchArchives)

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_router_link, {
            to: "/audit",
            class: "text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(ArrowLeft), { class: "w-4 h-4" }),
              _cache[1] || (_cache[1] = _createTextVNode(" Retour aux logs d'audit "))
            ]),
            _: 1,
            __: [1]
          }),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(ArchiveIcon), { class: "w-6 h-6 text-orange-600" })
            ]),
            _cache[2] || (_cache[2] = _createElementVNode("div", null, [
              _createElementVNode("h1", { class: "text-2xl sm:text-3xl font-bold text-dashboard-text" }, "Archives d'audit (MinIO)"),
              _createElementVNode("p", { class: "text-sm text-dashboard-muted mt-0.5" }, " Archives mensuelles JSONL gzippées. Rétention conseillée : 10 ans. ")
            ], -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_unref(Info), { class: "w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" }),
          _cache[3] || (_cache[3] = _createElementVNode("div", { class: "text-sm text-blue-900" }, [
            _createElementVNode("p", { class: "font-medium mb-1" }, "Politique de sauvegarde"),
            _createElementVNode("ul", { class: "text-blue-800 space-y-0.5 text-xs list-disc list-inside" }, [
              _createElementVNode("li", null, [
                _createTextVNode("Export "),
                _createElementVNode("strong", null, "automatique mensuel"),
                _createTextVNode(" du mois précédent (cron : 1er du mois à 04:00).")
              ]),
              _createElementVNode("li", null, [
                _createTextVNode("Stockage "),
                _createElementVNode("strong", null, "MinIO"),
                _createTextVNode(" dans "),
                _createElementVNode("code", { class: "bg-white px-1 rounded" }, "audit-archives/YYYY/MM/"),
                _createTextVNode(".")
              ]),
              _createElementVNode("li", null, "Données restent en base 24 mois glissants ; archives MinIO conservées 10 ans."),
              _createElementVNode("li", null, [
                _createTextVNode("Restauration possible via "),
                _createElementVNode("code", { class: "bg-white px-1 rounded" }, "python manage.py audit_restore"),
                _createTextVNode(".")
              ])
            ])
          ], -1))
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-xs text-dashboard-muted" }, "Archives stockées", -1)),
            _createElementVNode("p", _hoisted_8, _toDisplayString(archives.value.length), 1)
          ]),
          _createElementVNode("div", _hoisted_9, [
            _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-xs text-dashboard-muted" }, "Volume total", -1)),
            _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(formatBytes)(totalSize.value)), 1)
          ]),
          _createElementVNode("div", _hoisted_11, [
            _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-xs text-dashboard-muted" }, "Dernier export", -1)),
            _createElementVNode("p", _hoisted_12, _toDisplayString(lastExportDate.value || '—'), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("h2", _hoisted_14, [
            _createVNode(_unref(Zap), { class: "w-4 h-4 text-amber-500" }),
            _cache[7] || (_cache[7] = _createTextVNode(" Déclencher un export manuel "))
          ]),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("div", null, [
              _cache[8] || (_cache[8] = _createElementVNode("label", { class: "block text-xs text-dashboard-muted mb-1" }, "Mois à exporter (YYYY-MM)", -1)),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((exportMonth).value = $event)),
                type: "month",
                class: "px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm focus:outline-none focus:border-[#22282F]"
              }, null, 512), [
                [_vModelText, exportMonth.value]
              ])
            ]),
            _createElementVNode("button", {
              onClick: triggerExport,
              disabled: !exportMonth.value || exporting.value,
              class: "px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#22282F]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            }, [
              _createVNode(_unref(Upload), {
                class: _normalizeClass(['w-4 h-4', exporting.value && 'animate-pulse'])
              }, null, 8, ["class"]),
              _createTextVNode(" " + _toDisplayString(exporting.value ? 'Export en cours…' : 'Exporter maintenant'), 1)
            ], 8, _hoisted_16),
            _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-xs text-dashboard-muted" }, [
              _createTextVNode(" L'export est "),
              _createElementVNode("strong", null, "idempotent"),
              _createTextVNode(" : si l'archive existe déjà, elle sera écrasée. ")
            ], -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("h2", _hoisted_19, [
              _createVNode(_unref(ArchiveIcon), { class: "w-4 h-4" }),
              _cache[10] || (_cache[10] = _createTextVNode(" Archives mensuelles "))
            ]),
            _createElementVNode("button", {
              onClick: fetchArchives,
              disabled: loading.value,
              class: "text-dashboard-muted hover:text-dashboard-text disabled:opacity-50"
            }, [
              _createVNode(_unref(RefreshCw), {
                class: _normalizeClass(['w-4 h-4', loading.value && 'animate-spin'])
              }, null, 8, ["class"])
            ], 8, _hoisted_20)
          ]),
          (loading.value && archives.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, _cache[11] || (_cache[11] = [
                _createElementVNode("div", { class: "w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-3" }, null, -1),
                _createElementVNode("p", { class: "text-dashboard-muted" }, "Chargement…", -1)
              ])))
            : (error.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                  _createElementVNode("div", _hoisted_23, [
                    _createVNode(_unref(AlertCircle), { class: "w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" }),
                    _createElementVNode("div", null, [
                      _cache[12] || (_cache[12] = _createElementVNode("p", { class: "font-medium text-red-800" }, "Erreur", -1)),
                      _createElementVNode("p", _hoisted_24, _toDisplayString(error.value), 1)
                    ])
                  ])
                ]))
              : (archives.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                    _createVNode(_unref(ArchiveIcon), { class: "w-10 h-10 text-dashboard-muted mx-auto mb-3" }),
                    _cache[13] || (_cache[13] = _createElementVNode("p", { class: "text-dashboard-muted text-sm" }, "Aucune archive trouvée dans MinIO.", -1)),
                    _cache[14] || (_cache[14] = _createElementVNode("p", { class: "text-xs text-dashboard-muted mt-1" }, "Le premier export est généré le 1er du mois suivant la mise en service.", -1))
                  ]))
                : (_openBlock(), _createElementBlock("ul", _hoisted_26, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(archives.value, (archive) => {
                      return (_openBlock(), _createElementBlock("li", {
                        key: archive.path,
                        class: "px-4 py-3 hover:bg-dashboard-bg transition-colors flex items-center gap-4"
                      }, [
                        _createElementVNode("div", _hoisted_27, [
                          _createVNode(_unref(Calendar), { class: "w-5 h-5 text-orange-600" })
                        ]),
                        _createElementVNode("div", _hoisted_28, [
                          _createElementVNode("p", _hoisted_29, _toDisplayString(monthName(archive.year, archive.month)), 1),
                          _createElementVNode("p", _hoisted_30, _toDisplayString(archive.path), 1)
                        ]),
                        _createElementVNode("div", _hoisted_31, [
                          _createElementVNode("p", null, _toDisplayString(_unref(formatBytes)(archive.size_bytes)), 1),
                          (archive.modified_at)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_32, _toDisplayString(formatDate(archive.modified_at)), 1))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("a", {
                          href: downloadUrl(archive.year, archive.month),
                          target: "_blank",
                          rel: "noopener",
                          class: "px-3 py-1.5 bg-[#22282F] text-white rounded-lg text-xs font-medium hover:bg-[#22282F]/90 transition-colors flex items-center gap-1.5"
                        }, [
                          _createVNode(_unref(Download), { class: "w-3.5 h-3.5" }),
                          _cache[15] || (_cache[15] = _createTextVNode(" Télécharger "))
                        ], 8, _hoisted_33)
                      ]))
                    }), 128))
                  ]))
        ])
      ])
    ]),
    _: 1
  }))
}
}

})